import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

/** Exact split in rupees across `count` recipients (integer paise). */
function splitEqualTotal(total, count) {
  if (count <= 0 || total <= 0) return [];
  const cents = Math.round(round2(total) * 100);
  const base = Math.floor(cents / count);
  const amounts = [];
  let allocated = 0;
  for (let i = 0; i < count; i += 1) {
    if (i === count - 1) {
      amounts.push((cents - allocated) / 100);
    } else {
      amounts.push(base / 100);
      allocated += base;
    }
  }
  return amounts;
}

function applyEqualAmounts(rows, poolTotal) {
  const selected = rows.filter((r) => r.isSelected);
  if (selected.length === 0) {
    return rows.map((r) => ({ ...r, amount: 0 }));
  }
  const parts = splitEqualTotal(poolTotal, selected.length);
  const byId = {};
  selected.forEach((r, i) => {
    byId[r.id] = round2(parts[i] || 0);
  });
  return rows.map((r) => ({
    ...r,
    amount: r.isSelected ? round2(byId[r.id] || 0) : 0
  }));
}

function initialRowsFromApi(employees) {
  return employees.map((e) => ({
    id: e.id,
    name: e.name,
    employeeCode: e.employeeCode || '',
    profilePictureUrl: e.profilePictureUrl || null,
    isEmployer: !!e.isEmployer,
    isSelected: true,
    amount: 0
  }));
}

function PeopleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
      />
    </svg>
  );
}

function RupeeTabIcon() {
  return (
    <span className="pool-dist-tab-rupee" aria-hidden="true">
      ₹
    </span>
  );
}

function PoolDistributionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [poolBalance, setPoolBalance] = useState(0);
  const [employerIdNum, setEmployerIdNum] = useState(null);
  const [rows, setRows] = useState([]);
  const [splitMode, setSplitMode] = useState('equal');
  const [banner, setBanner] = useState('');
  const [validationError, setValidationError] = useState('');

  const load = useCallback(async (signal) => {
    setLoading(true);
    setBanner('');
    try {
      const axiosOpts = signal ? { signal } : {};
      const data = await webApi.getPoolEmployees(axiosOpts);
      if (signal?.aborted) return;
      const list = data?.employees || [];
      const pool = Number(data?.poolBalance ?? 0);
      setEmployerIdNum(Number(data?.employerId));
      setPoolBalance(pool);
      const base = initialRowsFromApi(list);
      setRows(applyEqualAmounts(base, pool));
      setSplitMode('equal');
    } catch (err) {
      if (signal?.aborted || isCanceledRequest(err)) return;
      setBanner(err.message || 'Failed to load pool data');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const totalDistributed = useMemo(
    () => round2(rows.reduce((s, r) => s + (Number(r.amount) || 0), 0)),
    [rows]
  );

  const remaining = round2(poolBalance - totalDistributed);

  useEffect(() => {
    if (totalDistributed > poolBalance + 0.001) {
      setValidationError('Total distributed amount cannot exceed available balance');
    } else {
      setValidationError('');
    }
  }, [totalDistributed, poolBalance]);

  const selectedWithAmount = rows.filter((r) => r.isSelected && Number(r.amount) > 0);
  const canSubmit =
    !loading &&
    !submitting &&
    totalDistributed > 0 &&
    totalDistributed <= poolBalance + 0.001 &&
    selectedWithAmount.length > 0;

  const setMode = (mode) => {
    setSplitMode(mode);
    if (mode === 'equal') {
      setRows((prev) => applyEqualAmounts(prev.map((r) => ({ ...r, isSelected: true })), poolBalance));
    } else {
      setRows((prev) => prev.map((r) => ({ ...r, isSelected: false, amount: 0 })));
    }
  };

  const fixCustomToggle = (id) => {
    setRows((prev) => {
      const next = prev.map((r) => {
        if (r.id !== id) return r;
        const sel = !r.isSelected;
        return {
          ...r,
          isSelected: sel,
          amount: sel ? r.amount : 0
        };
      });
      return next;
    });
  };

  const onToggle = (id) => {
    if (splitMode === 'equal') {
      setRows((prev) => {
        const next = prev.map((r) => (r.id === id ? { ...r, isSelected: !r.isSelected } : r));
        return applyEqualAmounts(next, poolBalance);
      });
    } else {
      fixCustomToggle(id);
    }
  };

  const onAmountChange = (id, text) => {
    if (splitMode !== 'custom') return;
    const n = parseFloat(String(text).replace(/[^0-9.]/g, ''));
    const val = Number.isNaN(n) ? 0 : round2(n);
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, amount: r.isSelected ? val : 0 } : r))
    );
  };

  const submit = async () => {
    if (!canSubmit || employerIdNum == null) return;
    setSubmitting(true);
    setBanner('');
    try {
      const distributions = rows
        .filter((r) => r.isSelected && Number(r.amount) > 0)
        .map((r) => {
          if (r.id.startsWith('employer_')) {
            return {
              employerId: Number(r.id.replace('employer_', '')),
              amount: Number(r.amount),
              isEmployer: true
            };
          }
          return {
            employeeId: Number(r.id.replace('emp_', '')),
            amount: Number(r.amount),
            isEmployer: false
          };
        });
      const totalAmount = round2(distributions.reduce((s, d) => s + d.amount, 0));
      await webApi.distributePool({
        distributions,
        totalAmount,
        notes: 'Web pool distribution'
      });
      setBanner('Pool distribution completed successfully.');
      await load();
    } catch (err) {
      setBanner(err.message || 'Pool distribution failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pool-dist-page">
        <p className="pool-dist-loading">Loading…</p>
      </div>
    );
  }

  return (
    <div className="pool-dist-page">
      <header className="pool-dist-topbar">
        <button type="button" className="pool-dist-back" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="pool-dist-topbar-title">Pool Distribution</h1>
        <span className="pool-dist-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="pool-dist-body">
        <div className="pool-dist-intro">
          <h2 className="pool-dist-h2">Pool Distribution</h2>
          <p className="pool-dist-sub">Distribute pool balance among employees</p>
        </div>

        {banner ? (
          <p
            className={`pool-dist-banner${banner.toLowerCase().includes('fail') ? ' pool-dist-banner--err' : ''}`}
            role="status"
          >
            {banner}
          </p>
        ) : null}

        <p className="pool-dist-section-label">Pool Balance Available</p>
        <div className="pool-dist-balance-card">
          <span className="pool-dist-balance-rupee">₹</span>
          <span className="pool-dist-balance-value">{poolBalance.toFixed(2)}</span>
        </div>
        <p className="pool-dist-hint">Pool balance will be distributed among selected employees</p>

        <div className="pool-dist-tabs">
          <button
            type="button"
            className={`pool-dist-tab${splitMode === 'equal' ? ' pool-dist-tab--on' : ''}`}
            onClick={() => setMode('equal')}
          >
            <PeopleIcon />
            <span>Equal</span>
          </button>
          <button
            type="button"
            className={`pool-dist-tab${splitMode === 'custom' ? ' pool-dist-tab--on' : ''}`}
            onClick={() => setMode('custom')}
          >
            <RupeeTabIcon />
            <span>Custom</span>
          </button>
        </div>

        <p className="pool-dist-mode-caption">{splitMode === 'equal' ? 'Split evenly' : 'Custom amounts'}</p>

        <ul className="pool-dist-list">
          {rows.map((row) => (
            <li key={row.id} className="pool-dist-row">
              <button
                type="button"
                className={`pool-dist-check${row.isSelected ? ' pool-dist-check--on' : ''}`}
                onClick={() => onToggle(row.id)}
                aria-pressed={row.isSelected}
                aria-label={row.isSelected ? 'Deselect' : 'Select'}
              >
                {row.isSelected ? '✓' : ''}
              </button>
              {row.profilePictureUrl ? (
                <img className="pool-dist-avatar" src={row.profilePictureUrl} alt="" />
              ) : (
                <div className="pool-dist-avatar pool-dist-avatar--ph" aria-hidden="true">
                  {row.isEmployer ? '🏦' : '👤'}
                </div>
              )}
              <div className="pool-dist-row-text">
                <span className="pool-dist-name">{row.name}</span>
                <span className="pool-dist-code">{row.employeeCode}</span>
              </div>
              <div className="pool-dist-amt-wrap">
                <span className="pool-dist-amt-prefix">₹</span>
                {splitMode === 'equal' || !row.isSelected ? (
                  <span className="pool-dist-amt-readout">
                    {row.isSelected && row.amount ? row.amount.toFixed(2) : '0'}
                  </span>
                ) : (
                  <input
                    className="pool-dist-amt-input"
                    type="text"
                    inputMode="decimal"
                    value={row.amount ? String(row.amount) : ''}
                    placeholder="0"
                    onChange={(e) => onAmountChange(row.id, e.target.value)}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>

        {validationError ? (
          <p className="pool-dist-validation" role="alert">
            {validationError}
          </p>
        ) : null}

        <div className="pool-dist-remaining">Remaining: ₹{remaining.toFixed(2)}</div>

        <button type="button" className="pool-dist-submit" disabled={!canSubmit} onClick={submit}>
          {submitting ? 'Processing…' : 'Distribute Pool Amount'}
        </button>
      </div>
    </div>
  );
}

export default PoolDistributionPage;
